import { useEffect, useState, type ReactNode } from 'react'
import { Api, UserType } from '../api'

// The one top nav for the signed-in app pages, so every page shows the same
// items in the same order. Pages add their own buttons via `actions`.

export type NavPage = 'status' | 'captures' | 'submissions' | 'fishbrain-export' | 'settings'

const NAV_ITEMS: { page: NavPage; hash: string; label: string; adminOnly?: boolean }[] = [
  { page: 'status', hash: '#status', label: 'Devices' },
  { page: 'captures', hash: '#captures', label: 'Captures' },
  { page: 'submissions', hash: '#submissions', label: 'Submissions' },
  { page: 'fishbrain-export', hash: '#fishbrain-export', label: 'Fishbrain export' },
  { page: 'settings', hash: '#settings', label: 'Settings', adminOnly: true },
]

export default function AppHeader({ active, onNavigate, onLogout, actions, onLogoClick, containerClassName = 'max-w-6xl mx-auto' }: {
  active?: NavPage
  onNavigate: (hash: string) => void
  onLogout: () => void
  actions?: ReactNode
  // Defaults to going to #status.
  onLogoClick?: () => void
  // Width of the inner row; pass '' for full width.
  containerClassName?: string
}) {
  const [isAdmin, setIsAdmin] = useState(false)

  useEffect(() => {
    Api.getCurrentUser().then(r => setIsAdmin(r.user.type === UserType.Admin)).catch(() => {})
  }, [])

  return (
    <header className="sticky top-0 z-10 bg-white border-b border-stone-200">
      <div className={`${containerClassName} px-4 py-3 flex items-center justify-between gap-4`}>
        <button onClick={onLogoClick ?? (() => onNavigate('#status'))}
          className="flex items-center gap-2.5 cursor-pointer shrink-0">
          <img src="/bear_icon_transparent.svg" alt="" className="h-9 w-auto" />
          <h1 className="font-display font-black text-lg tracking-widest text-bear uppercase leading-none hidden sm:block">
            Kodiak
          </h1>
        </button>
        <div className="flex items-center gap-2 min-w-0">
          {actions}
          <nav className="flex items-center gap-1 overflow-x-auto">
            {NAV_ITEMS.filter(item => isAdmin || !item.adminOnly).map(item => (
              <NavBtn key={item.page} onClick={() => onNavigate(item.hash)} active={item.page === active}>
                {item.label}
              </NavBtn>
            ))}
            <NavBtn onClick={onLogout}>Sign out</NavBtn>
          </nav>
        </div>
      </div>
    </header>
  )
}

function NavBtn({ children, onClick, active }: { children: ReactNode; onClick: () => void; active?: boolean }) {
  return (
    <button onClick={onClick} aria-current={active ? 'page' : undefined}
      className={`font-mono text-[10px] tracking-[0.15em] uppercase whitespace-nowrap ${active ? 'text-bear' : 'text-stone-500'}
        hover:text-bear transition-colors px-2 py-1 rounded cursor-pointer`}>
      {children}
    </button>
  )
}
